// Shop Configuration - Easily customizable settings for the landing page

const SHOP_CONFIG_STORAGE_KEY = 'cnf_shop_config';

// Default configuration
const defaultShopConfig = {
  // Business Information
  name: 'CNF Auto LLC',
  tagline: 'Dealer-Level Expertise at Independent Shop Prices',
  phone: '305-316-0393',
  email: 'info@cnfautosuite.com',
  address: {
    street: '123 Main Street',
    city: 'Miami',
    state: 'FL',
    zip: '33101',
  },

  // Branding & Visual Identity
  branding: {
    logo: '', // URL or base64 image data
    favicon: '',
    heroBackground: '', // Hero section background image
    theme: 'performance', // performance, premium, community, tech
    customColors: {
      primary: '#ef4444', // Red (default performance theme)
      secondary: '#111827', // Dark
      accent: '#f59e0b', // Orange/Yellow
      background: '#ffffff',
      text: '#111827',
    },
  },

  // Gallery - Work Showcase
  gallery: {
    enabled: true,
    images: [] as Array<{
      id: string;
      url: string;
      title: string;
      category: string;
      description?: string;
      type?: 'image' | 'video'; // 'image' (default) or 'video' for YouTube/Vimeo embeds
      videoProvider?: 'youtube' | 'vimeo'; // Only needed if type is 'video'
      isBefore?: boolean;
      afterImageId?: string;
    }>,
    categories: ['Engine Work', 'Transmission', 'Electrical', 'Brakes', 'Suspension', 'Diagnostics', 'General Repair'],
  },

  // Testimonials
  testimonials: {
    enabled: true,
    reviews: [] as Array<{
      id: string;
      name: string;
      rating: number;
      text: string;
      date?: string;
      avatar?: string;
    }>,
  },

  // Business Hours (placeholder for future)
  hours: {
    weekday: '8:00 AM - 6:00 PM',
    saturday: '9:00 AM - 4:00 PM',
    sunday: 'Closed',
  },

  // Services
  services: {
    heavyLine: {
      title: 'Heavy Line & Advanced Diagnostics',
      description: 'Dealer-level expertise for complex repairs',
      items: [
        'Engine Repair & Replacement',
        'Transmission Repair & Replacement',
        'Advanced Electrical Diagnosis',
        'Communication & Network Diagnosis',
        'Check Engine Light Diagnosis',
        'A/C System Diagnosis & Repair',
        'Hard-to-Diagnose Issues',
      ],
    },
    maintenance: {
      title: 'Complete Maintenance Services',
      description: 'From routine maintenance to complex repairs',
      items: [
        'Brake Service & Repair',
        'Wheel Bearing Replacement',
        'Suspension Repair',
        'Rack & Pinion Service',
        'CV Axle Replacement',
        'Spark Plug Replacement',
        'Oil Changes & Fluid Service',
        'And Much More!',
      ],
    },
  },

  // Brands Supported
  brands: {
    simple: [
      { name: 'Ford', popular: true },
      { name: 'Chevrolet', popular: true },
      { name: 'Toyota', popular: true },
      { name: 'Lexus', popular: true },
    ],
    complex: [
      { name: 'Mercedes-Benz', premium: true },
      { name: 'Porsche', premium: true },
      { name: 'BMW', premium: true },
      { name: 'Audi', premium: true },
    ],
  },

  // Pre-Purchase Inspection
  prePurchaseInspection: {
    title: 'Pre-Purchase Inspection',
    description: 'We come to you! Get peace of mind before buying your next vehicle.',
    startingPrice: 250,
    features: [
      'Comprehensive vehicle inspection',
      'We travel to the vehicle location',
      'Detailed inspection report',
      'Expert buying recommendations',
      'Pricing varies by location',
    ],
  },

  // Value Propositions
  guarantees: {
    priceMatch: {
      title: 'Price Match Guarantee',
      description: 'We will beat or match any established shop quote',
      subtitle: 'Get dealer-quality service without the dealer price tag',
    },
    oneStopShop: {
      title: 'One-Stop Shop',
      description: 'From simple maintenance to complex repairs - we handle it all',
      subtitle: 'No job too big or too small',
    },
  },

  // Call to Action
  cta: {
    primary: 'Call Now for Expert Service',
    secondary: 'Get a Free Quote',
    phoneDisplay: '(305) 316-0393',
  },

  // Social Media (placeholder for future)
  social: {
    facebook: '',
    instagram: '',
    google: '',
  },
};

// Theme Color Presets
export const themePresets = {
  performance: {
    name: 'Performance',
    description: 'Bold, energetic red and black racing theme',
    colors: {
      primary: '#ef4444', // Red
      secondary: '#111827', // Black
      accent: '#f59e0b', // Orange
      background: '#ffffff',
      text: '#111827',
    },
    gradient: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
  },
  premium: {
    name: 'Premium',
    description: 'Sophisticated navy and gold luxury theme',
    colors: {
      primary: '#1e40af', // Navy
      secondary: '#d97706', // Gold
      accent: '#0ea5e9', // Light Blue
      background: '#ffffff',
      text: '#111827',
    },
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
  },
  community: {
    name: 'Community',
    description: 'Warm, trustworthy blue and orange theme',
    colors: {
      primary: '#2563eb', // Blue
      secondary: '#ea580c', // Orange
      accent: '#10b981', // Green
      background: '#ffffff',
      text: '#111827',
    },
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  },
  tech: {
    name: 'Tech',
    description: 'Modern electric blue and black tech theme',
    colors: {
      primary: '#06b6d4', // Cyan
      secondary: '#0f172a', // Dark Blue-Black
      accent: '#8b5cf6', // Purple
      background: '#ffffff',
      text: '#111827',
    },
    gradient: 'linear-gradient(135deg, #0891b2 0%, #164e63 100%)',
  },
};

export type ShopConfig = typeof defaultShopConfig;

/**
 * Get current shop configuration (merges localStorage with defaults)
 */
const getShopConfig = (): ShopConfig => {
  try {
    const stored = localStorage.getItem(SHOP_CONFIG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Deep merge stored config with defaults to handle new fields
      return { ...defaultShopConfig, ...parsed };
    }
  } catch (error) {
    console.error('Error loading shop config:', error);
  }
  return defaultShopConfig;
};

/**
 * Save shop configuration to localStorage
 */
export const saveShopConfig = (config: ShopConfig): void => {
  try {
    localStorage.setItem(SHOP_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Error saving shop config:', error);
  }
};

/**
 * Reset shop configuration to defaults
 */
export const resetShopConfig = (): void => {
  try {
    localStorage.removeItem(SHOP_CONFIG_STORAGE_KEY);
  } catch (error) {
    console.error('Error resetting shop config:', error);
  }
};

/**
 * Get theme colors based on current theme selection
 */
export const getThemeColors = () => {
  const config = getShopConfig();
  const theme = config.branding?.theme || 'performance';

  // If custom colors are set, use them
  if (config.branding?.customColors) {
    return config.branding.customColors;
  }

  // Otherwise use preset
  return themePresets[theme as keyof typeof themePresets]?.colors || themePresets.performance.colors;
};

/**
 * Get theme gradient
 */
export const getThemeGradient = () => {
  const config = getShopConfig();
  const theme = config.branding?.theme || 'performance';
  return themePresets[theme as keyof typeof themePresets]?.gradient || themePresets.performance.gradient;
};

// Export the active configuration
export const shopConfig = getShopConfig();
